/**
 * =============================================================================
 * CustomVariantPicker — Full-Featured Variant Selection Component
 * Replaces theme's VariantSelects for the main product page.
 * Handles: searchable dropdowns, image swatches, media gallery sync,
 * price/inventory/sku updates, stock countdown, sold-out badge, share URL,
 * add-to-cart state, volume pricing, card variant updates, and more.
 * =============================================================================
 */

class CustomVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    // Apply image circle size CSS variable from data attribute (set by Liquid schema)
    const size = parseInt(this.dataset.imageCircleSize, 10) || 52;
    this.style.setProperty('--image-circle-size', `${size}px`);
    this.initSearchableDropdowns();

    // Initial filter for sequence-based grouping
    const runFilter = () => {
        if (typeof this.updateOptions === 'function') this.updateOptions();
        if (typeof this.updateMasterId === 'function') this.updateMasterId();
        
        const variantId = this.currentVariant ? this.currentVariant.id : '';
        const gallery = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (gallery && typeof gallery.filterMedia === 'function') {
            gallery.filterMedia(variantId);
        }
    };

    if (document.readyState === 'complete') {
        runFilter();
    } else {
        window.addEventListener('load', runFilter);
    }
  }

  // ============================================================
  // SEARCHABLE DROPDOWN INIT
  // ============================================================

  initSearchableDropdowns() {
    const containers = this.querySelectorAll('.searchable-variant-container');

    containers.forEach(container => {
      const trigger           = container.querySelector('.variant-search-trigger');
      const menu              = container.querySelector('.variant-search-menu');
      const input             = container.querySelector('.variant-search-input');
      const select            = container.querySelector('select');
      const selectedValueText = container.querySelector('.variant-search-selected-value');
      const labelSpan         = container.querySelector('.form__label span');

      if (!trigger || !menu || !input) return;

      // --- Open / Close Trigger ---
      // Use onclick= (property assignment) so re-init safely OVERWRITES the handler
      // instead of stacking duplicate addEventListener calls which cause toggle conflict.
      trigger.onclick = (e) => {
        e.stopPropagation();
        const isOpen = trigger.classList.toggle('is-open');
        if (isOpen) {
          // Close all other open dropdowns on this picker
          this.querySelectorAll('.variant-search-trigger.is-open').forEach(t => {
            if (t !== trigger) t.classList.remove('is-open');
          });
          setTimeout(() => input.focus(), 50);
        }
      };

      // --- Search Input Filter --- (oninput = overwrite-safe)
      input.oninput = () => {
        const q = input.value.trim().toLowerCase();
        container.querySelectorAll('.variant-search-item').forEach(item => {
          item.classList.toggle('is-hidden', !item.textContent.trim().toLowerCase().includes(q));
        });
      };

      // --- Keyboard: Escape closes --- (onkeydown = overwrite-safe)
      input.onkeydown = (e) => {
        if (e.key === 'Escape') {
          trigger.classList.remove('is-open');
          input.value = '';
          container.querySelectorAll('.variant-search-item').forEach(i => i.classList.remove('is-hidden'));
        }
      };

      // --- Prevent menu close on internal click --- (onclick = overwrite-safe)
      menu.onclick = (e) => e.stopPropagation();

      // --- Item Selection ---
      // Clone and replace each item to strip any old event listeners before re-binding.
      // This is called on every init (including after model change re-renders items).
      container.querySelectorAll('.variant-search-item').forEach(item => {
        const fresh = item.cloneNode(true);
        item.parentNode.replaceChild(fresh, item);
        fresh.onclick = (e) => {
          e.stopPropagation();
          if (fresh.classList.contains('hidesold')) return;

          const val  = fresh.getAttribute('data-value');
          const text = fresh.textContent.trim();

          if (selectedValueText) selectedValueText.textContent = text;
          if (labelSpan)         labelSpan.textContent = text;

          if (select) {
            select.value = val;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }

          container.querySelectorAll('.variant-search-item').forEach(i => i.classList.remove('is-selected'));
          fresh.classList.add('is-selected');

          trigger.classList.remove('is-open');
          input.value = '';
          container.querySelectorAll('.variant-search-item').forEach(i => i.classList.remove('is-hidden'));
        };
      });
    });

    // --- Global click → close all open dropdowns ---
    if (!this._globalClickBound) {
      this._globalClickBound = true;
      document.addEventListener('click', () => {
        this.querySelectorAll('.variant-search-trigger.is-open').forEach(t => {
          t.classList.remove('is-open');
        });
      });
    }
  }

  // ============================================================
  // CORE VARIANT CHANGE HANDLER
  // ============================================================

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();

    // ---------------------------------------------------------------
    // SMART AUTO-SELECT: If model changed and old color doesn't exist
    // for the new model (currentVariant is undefined), automatically
    // select the first available color for the new model and re-trigger.
    // This ensures color swatches always refresh after model change.
    // ---------------------------------------------------------------
    if (!this.currentVariant) {
      const autoSelected = this.autoSelectFirstAvailableForCurrentOptions();
      if (autoSelected) return; // autoSelect will fire a new change event
      // Truly unavailable combo
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
      return;
    }

    this.updateSelectedSwatchValue(event);
    this.updateVariantLabel(event);
    this.toggleAddButton(true, '', false);
    this.updateSoldOutBadge();
    this.updateSku();
    this.updateStockCountdown();

    if (this.dataset.layout === 'card') {
      this.updateMedia();
    }
    this.updateURL();
    this.updateShareUrl();
    this.updateVariantInput();
    this.updateCardURL();
    this.renderProductInfo();

    // Sequence-based gallery filtering
    const gallery = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
    if (gallery && typeof gallery.filterMedia === 'function') {
        gallery.filterMedia(this.currentVariant.id);
    }
  }

  // ---------------------------------------------------------------
  // AUTO-SELECT FIRST AVAILABLE VARIANT for current option1 (model)
  // When model changes, find the first available variant for that
  // model and programmatically check the correct color radio input.
  // Returns true if it found and selected something (caller should return).
  // ---------------------------------------------------------------
  autoSelectFirstAvailableForCurrentOptions() {
    const variantData    = this.getVariantData();
    const currentOption1 = this.options[0]; // current model/option1 value

    // Find first available variant that matches the current option1 (model)
    const firstAvailable = variantData.find(v =>
      v.available && v.option1 === currentOption1
    );
    if (!firstAvailable) return false;

    // Go through all radio inputs inside fieldsets and check the correct one.
    // Each fieldset has a name attribute matching the option name.
    // We map that name to the variant's option value at the right index.
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    let didSelect = false;

    fieldsets.forEach(fieldset => {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;

      // Get option name from the input's name attribute (e.g. "Color")
      const optionName = inputs[0]?.name;
      if (!optionName) return;

      // Find which option index this fieldset corresponds to (0-based)
      const optionIndex = this.getVariantData()[0]?.options
        ? firstAvailable.options.findIndex((_, i) => {
            // Match by checking if any input value matches any of firstAvailable's options
            return inputs.some(inp => inp.value === firstAvailable.options[i]);
          })
        : -1;

      // Simpler approach: find the right option value from firstAvailable
      // by checking which option this fieldset's name corresponds to
      const productOptions = Object.keys(firstAvailable).filter(k => k.startsWith('option'));
      let targetValue = null;
      for (let i = 1; i <= 3; i++) {
        const key = `option${i}`;
        if (firstAvailable[key] !== undefined) {
          // Does this fieldset contain an input with this value?
          if (inputs.some(inp => inp.value === firstAvailable[key])) {
            targetValue = firstAvailable[key];
            break;
          }
        }
      }

      if (!targetValue) return;

      const targetInput = inputs.find(inp => inp.value === targetValue);
      if (targetInput && !targetInput.checked) {
        targetInput.checked = true;
        didSelect = true;
      }
    });

    if (didSelect) {
      // Re-trigger so the full onVariantChange runs with the new combo
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return didSelect;
  }

  // ============================================================
  // OPTIONS & VARIANT MATCHING
  // ============================================================

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value;
      }
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find(r => r.checked)?.value;
      }
    });
  }

  updateMasterId() {
    this.variantData = this.variantData || JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false);
    });
  }

  // ============================================================
  // LABEL UPDATES (form__label span, legend span, swatch value)
  // ============================================================

  updateVariantLabel(event) {
    const target = event?.target;
    if (!target) return;

    // Fieldset-based (button / swatch / image-circle pickers)
    const fieldset = target.closest('fieldset');
    if (fieldset) {
      const span = fieldset.querySelector('legend span') || fieldset.querySelector('.form__label span');
      if (span) span.innerHTML = target.value;
    }

    // Searchable dropdown container
    const searchContainer = target.closest('.searchable-variant-container');
    if (searchContainer) {
      const span = searchContainer.querySelector('.form__label span');
      if (span) span.innerHTML = target.value;
    }
  }

  updateSelectedSwatchValue({ target }) {
    if (!target) return;
    const { name, value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = this.querySelector(
        `[data-selected-dropdown-swatch="${name}"] > .swatch`
      );
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }
      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = this.querySelector(`[data-selected-swatch-value="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  // ============================================================
  // URL & SHARE
  // ============================================================

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateURLParameter(url, param, paramVal) {
    let newAdditionalURL = '';
    const tempArray = url.split('?');
    const baseURL = tempArray[0];
    const additionalURL = tempArray[1];
    let temp = '';
    if (additionalURL) {
      const parts = additionalURL.split('&');
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].split('=')[0] !== param) {
          newAdditionalURL += temp + parts[i];
          temp = '&';
        }
      }
    }
    return baseURL + '?' + newAdditionalURL + temp + param + '=' + paramVal;
  }

  // ============================================================
  // VARIANT INPUT UPDATE (product form hidden input, card price)
  // ============================================================

  updateVariantInput() {
    // === CARD LAYOUT ===
    if (this.dataset.layout === 'card') {
      const card = this.closest('.card');
      const productForms = card.querySelectorAll(
        `#ProductInfo-${this.dataset.section}-${this.dataset.product}`
      );
      productForms.forEach((productForm) => {
        productForm.querySelectorAll('input[name="id"]').forEach(el => {
          el.value = this.currentVariant.id;
        });
        productForm.querySelectorAll('select[name="id"]').forEach(el => {
          el.value = this.currentVariant.id;
        });

        Array.from(this.querySelectorAll('fieldset')).forEach(fieldset => {
          fieldset.querySelectorAll('label').forEach(label => label.classList.remove('active'));
        });

        const container = this.closest('.grid__item');
        if (!container) return;

        const optionData       = container.querySelectorAll('select option');
        const regularPrice     = container.querySelector('.price .price__container .price__regular .price-item--regular');
        const saleRegularPrice = container.querySelector('.price .price__container .price__sale .price-item--sale');
        const salePrice        = container.querySelector('.price .price__container .price__sale .price-item--regular');
        const wbunitPrice      = container.querySelector('.price .price__container .unit-price .cardunitp');
        const wbunitValue      = container.querySelector('.price .price__container .unit-price .cardunitv');
        const wbPercentBadge   = container.querySelector('.card__badge .percent__badge-sale');
        const wbAmountBadge    = container.querySelector('.card__badge .amount__badge-sale');

        optionData.forEach((data) => {
          if (data.value != this.currentVariant.id) return;

          if (data.dataset.cprice && data.dataset.price !== data.dataset.cprice) {
            if (saleRegularPrice) saleRegularPrice.innerHTML = data.dataset.price;
            if (salePrice)        salePrice.innerHTML        = data.dataset.cprice;
            if (data.dataset.damount && wbAmountBadge) {
              wbAmountBadge.innerHTML = data.dataset.damount;
              wbAmountBadge.classList.remove('hidden');
            }
            if (data.dataset.percent && wbPercentBadge) {
              wbPercentBadge.innerHTML = data.dataset.percent;
              wbPercentBadge.classList.remove('hidden');
            }
            container.querySelector('.price')?.classList.add('price--on-sale');
          } else {
            if (regularPrice) regularPrice.innerHTML = data.dataset.price;
            if (wbPercentBadge) wbPercentBadge.classList.add('hidden');
            if (wbAmountBadge)  wbAmountBadge.classList.add('hidden');
            container.querySelector('.price')?.classList.remove('price--on-sale');
          }

          if (data.dataset.unitprice && wbunitPrice) {
            wbunitPrice.innerHTML = data.dataset.unitprice;
            container.querySelector('.price')?.classList.add('price--on-sale');
          }
          if (data.dataset.unitvalue && wbunitValue) {
            wbunitValue.innerHTML = data.dataset.unitvalue;
            container.querySelector('.price')?.classList.add('price--on-sale');
          }
        });
      });
      return;
    }

    // === STANDARD PRODUCT PAGE ===
    document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ============================================================
  // CARD URL UPDATE
  // ============================================================

  updateCardURL() {
    if (this.dataset.layout !== 'card') return;
    const card = this.closest('.card');
    if (!card) return;

    const productURL      = this.updateURLParameter(this.dataset.url, 'variant', this.currentVariant.id);
    const productAnchor   = card.querySelector('.card__media a');
    const titleAnchor     = card.querySelector('.card__heading a');
    const quickview       = card.querySelector('.js-wbquickview-link');

    if (productAnchor) productAnchor.setAttribute('href', productURL);
    if (titleAnchor)   titleAnchor.setAttribute('href', productURL);
    if (quickview)     quickview.setAttribute('variant-id', this.currentVariant.id);
  }

  // ============================================================
  // SKU
  // ============================================================

  updateSku() {
    if (!this.currentVariant) return;
    const productMain = this.closest('.product');
    if (productMain && productMain.querySelector('.variant-sku')) {
      productMain.querySelector('.variant-sku').innerHTML = this.currentVariant.sku;
    }
  }

  // ============================================================
  // STOCK COUNTDOWN
  // ============================================================

  updateStockCountdown() {
    if (!this.currentVariant) return;
    const productMain = this.closest('.product');
    if (!productMain || !productMain.querySelector('.wbstockcount')) return;

    const variantsDataEl       = productMain.querySelector('#product-variants');
    const stockConfigEl        = productMain.querySelector('#product-stock-countdown-config');
    if (!variantsDataEl || !stockConfigEl) return;

    const variantsData         = JSON.parse(variantsDataEl.innerHTML);
    const stockCountdownConfig = JSON.parse(stockConfigEl.innerHTML);
    const variant              = this.currentVariant;
    const countdownWrapper     = productMain.querySelector('.wbstockcount');
    const variantStock         = variantsData[variant.id];

    if (!variantStock) return;

    if (variantStock['q'] > 0 && variant.inventory_management === 'shopify') {
      productMain.querySelector('.wbstockcount').classList.remove('hidden');
      productMain.querySelector('.wbstockinfo-bar')?.classList.remove('hidden');

      const infoEl = countdownWrapper.querySelector('.wbstockinfo');
      const barEl  = countdownWrapper.querySelector('.wbstockinfo-probar');

      if (variantStock['q'] > stockCountdownConfig.limited_stock_cut_off) {
        // High Stock
        infoEl.classList.remove('wbstockinfo_low', 'wbstockinfo_soon');
        infoEl.classList.add('wbstockinfo_high');
        infoEl.innerHTML = stockCountdownConfig.instock_message.replace('{stock_number}', variantStock['q']);
      } else {
        // Low Stock
        infoEl.classList.replace('wbstockinfo_soon', 'wbstockinfo_low');
        infoEl.classList.add('wbstockinfo_low');
        infoEl.innerHTML = stockCountdownConfig.limited_stock_message.replace('{stock_number}', variantStock['q']);
      }
      if (barEl) barEl.style.width = ((variantStock['q'] * 100) / stockCountdownConfig.max_stock) + '%';

    } else if (variantStock['p'] === 'continue') {
      productMain.querySelector('.wbstockcount').classList.remove('hidden');
      productMain.querySelector('.wbstockinfo-bar')?.classList.add('hidden');
      const infoEl = countdownWrapper.querySelector('.wbstockinfo');
      infoEl.classList.remove('wbstockinfo_low', 'wbstockinfo_high');
      infoEl.classList.add('wbstockinfo_soon');
      infoEl.innerHTML = stockCountdownConfig.continue_selling_message;
    } else {
      productMain.querySelector('.wbstockcount').classList.add('hidden');
    }
  }

  // ============================================================
  // SOLD OUT BADGE (product cards)
  // ============================================================

  updateSoldOutBadge() {
    const card = this.closest('.card-wrapper');
    if (!card) return;
    const soldOutBadge = card.querySelectorAll('.soldout_product_badge');
    if (soldOutBadge && soldOutBadge.length === 1) {
      const isAvailable = this.currentVariant && this.currentVariant.available;
      soldOutBadge[0].classList.toggle('hidden', isAvailable);
    }
  }

  // ============================================================
  // ADD TO CART BUTTON
  // ============================================================

  toggleAddButton(disable = true, text, modifyClass = true) {
    let selector = `product-form-${this.dataset.section}`;
    if (this.dataset.layout === 'card') {
      selector = `product-form-${this.dataset.section}-${this.dataset.product}`;
    }
    const productForm = document.getElementById(selector);
    if (!productForm) return;

    const addButton     = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }
    if (!modifyClass) return;
  }

  setUnavailable() {
    let selector = `product-form-${this.dataset.section}`;
    if (this.dataset.layout === 'card') {
      selector = `product-form-${this.dataset.section}-${this.dataset.product}`;
    }

    const button = document.getElementById(selector);
    if (!button) return;

    const addButton     = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');

    const price       = document.getElementById(`price-${this.dataset.section}`);
    const inventory   = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku         = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem= document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote  = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules    = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (inventory)   inventory.classList.add('hidden');
    if (sku)         sku.classList.add('hidden');
    if (pricePerItem)pricePerItem.classList.add('hidden');
    if (volumeNote)  volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules)    qtyRules.classList.add('hidden');
  }

  // ============================================================
  // MEDIA GALLERY UPDATE (full diffing algorithm from original theme)
  // ============================================================

  updateMedia(html) {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    // === CARD LAYOUT ===
    if (this.dataset.layout === 'card') {
      const card = this.closest('.card');
      if (!card) return;
      const newMedia = card.querySelector(
        `[data-media-id="${this.dataset.section}-${this.dataset.product}-${this.currentVariant.featured_media.id}"]`
      );
      if (!newMedia) return;
      const parent = newMedia.parentElement;
      if (parent.firstChild === newMedia) return;
      parent.prepend(newMedia);
      return;
    }

    // === STANDARD PRODUCT PAGE ===
    if (!html) return;

    const mediaGallerySource      = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
    const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);

    const refreshSourceData = () => {
      const items  = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
      const set    = new Set(items.map(item => item.dataset.mediaId));
      const map    = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
      return [items, set, map];
    };

    if (mediaGallerySource && mediaGalleryDestination) {
      let [srcItems, srcSet, srcMap] = refreshSourceData();
      const destItems  = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
      const destSet    = new Set(destItems.map(({ dataset }) => dataset.mediaId));
      let shouldRefresh = false;

      // Add items from new data not present in DOM
      for (let i = destItems.length - 1; i >= 0; i--) {
        if (!srcSet.has(destItems[i].dataset.mediaId)) {
          mediaGallerySource.prepend(destItems[i]);
          shouldRefresh = true;
        }
      }

      // Remove items from DOM not present in new data
      for (let i = 0; i < srcItems.length; i++) {
        if (!destSet.has(srcItems[i].dataset.mediaId)) {
          srcItems[i].remove();
          shouldRefresh = true;
        }
      }

      if (shouldRefresh) [srcItems, srcSet, srcMap] = refreshSourceData();

      // Sort to match new data order
      destItems.forEach((destItem, destIndex) => {
        const srcData = srcMap.get(destItem.dataset.mediaId);
        if (srcData && srcData.index !== destIndex) {
          mediaGallerySource.insertBefore(
            srcData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`)
          );
          [srcItems, srcSet, srcMap] = refreshSourceData();
        }
      });
    }

    // Set active/featured media
    document
      .querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)
      ?.setActiveMedia?.(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);

    // Update media modal
    const modalContent    = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    const newModalContent = html.querySelector('product-modal');
    if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
  }

  // ============================================================
  // RENDER PRODUCT INFO (main AJAX section fetch)
  // ============================================================

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const originalSection    = this.dataset.originalSection;
    const sectionId          = this.dataset.section;
    const fetchSectionId     = originalSection || sectionId;

    // === CARD LAYOUT — lightweight fetch ===
    if (this.dataset.layout === 'card') {
      fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${fetchSectionId}`)
        .then(r => r.text())
        .then(responseText => {
          if (this.currentVariant.id !== requestedVariantId) return;
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cardSectionId = `${fetchSectionId}-${this.dataset.product}`;

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: cardSectionId, html, variant: this.currentVariant }
            });
          }
        });
      return;
    }

    // === STANDARD PRODUCT PAGE — full section fetch ===
    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${fetchSectionId}`)
      .then(r => r.text())
      .then(responseText => {
        // Guard: currentVariant may have become undefined if user changed
        // variant rapidly while fetch was in-flight (race condition)
        if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // --- Price ---
        const priceDest   = document.querySelectorAll(`#price-${sectionId}`);
        const priceSrc    = html.querySelectorAll(`#price-${fetchSectionId}`);
        if (priceSrc.length === priceDest.length && priceSrc.length > 0) {
          priceSrc.forEach((src, i) => { priceDest[i].innerHTML = src.innerHTML; });
        }
        const priceEl = document.getElementById(`price-${sectionId}`);
        if (priceEl) priceEl.classList.remove('hidden');

        // --- SKU ---
        const skuSrc  = html.getElementById(`Sku-${fetchSectionId}`);
        const skuDest = document.getElementById(`Sku-${sectionId}`);
        if (skuSrc && skuDest) {
          skuDest.innerHTML = skuSrc.innerHTML;
          skuDest.classList.toggle('visibility-hidden', skuSrc.classList.contains('visibility-hidden'));
        }

        // --- Inventory ---
        const invSrc  = html.getElementById(`Inventory-${fetchSectionId}`);
        const invDest = document.getElementById(`Inventory-${sectionId}`);
        if (invSrc && invDest) {
          invDest.innerHTML = invSrc.innerHTML;
          invDest.classList.toggle('visibility-hidden', invSrc.innerText === '');
        }

        // --- Volume Pricing ---
        const volumeSrc  = html.getElementById(`Volume-${fetchSectionId}`);
        const volumeDest = document.getElementById(`Volume-${sectionId}`);
        if (volumeSrc && volumeDest) {
          volumeDest.innerHTML = volumeSrc.innerHTML;
          volumeDest.classList.remove('hidden');
        }

        // --- Price Per Item ---
        const ppiSrc  = html.getElementById(`Price-Per-Item-${fetchSectionId}`);
        const ppiDest = document.getElementById(`Price-Per-Item-${sectionId}`);
        if (ppiSrc && ppiDest) {
          ppiDest.innerHTML = ppiSrc.innerHTML;
          ppiDest.classList.toggle('visibility-hidden', ppiSrc.classList.contains('visibility-hidden'));
        }

        // --- Volume Note & Qty Rules ---
        const volumeNote = document.getElementById(`Volume-Note-${sectionId}`);
        const qtyRules   = document.getElementById(`Quantity-Rules-${sectionId}`);
        if (volumeNote) volumeNote.classList.remove('hidden');
        if (qtyRules)   qtyRules.classList.remove('hidden');

        // --- Media Gallery ---
        this.updateMedia(html);

        // --- Add to Cart Button ---
        const addButtonSrc = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(
          addButtonSrc ? addButtonSrc.hasAttribute('disabled') : true,
          window.variantStrings.soldOut
        );

        // --- CRITICAL: Update Variant Picker (color swatches) from server HTML ---
        // When the model/option1 changes, Liquid re-renders the color availability
        // server-side. We replace only the image_circle fieldsets + search items
        // so swatches always match the newly selected model.
        const pickerSrc  = html.getElementById(`variant-selects-${fetchSectionId}`);
        const pickerDest = document.getElementById(`variant-selects-${sectionId}`);
        if (pickerSrc && pickerDest) {
          // Replace image circle fieldsets (color swatches)
          const srcFieldsets  = pickerSrc.querySelectorAll('.variant-image-circle-container');
          const destFieldsets = pickerDest.querySelectorAll('.variant-image-circle-container');
          srcFieldsets.forEach((srcFieldset, i) => {
            if (destFieldsets[i]) {
              destFieldsets[i].innerHTML = srcFieldset.innerHTML;
            }
          });

          // Replace searchable dropdown results (for option2+ search items)
          const srcResults  = pickerSrc.querySelectorAll('.variant-search-results');
          const destResults = pickerDest.querySelectorAll('.variant-search-results');
          srcResults.forEach((srcResult, i) => {
            if (destResults[i]) {
              destResults[i].innerHTML = srcResult.innerHTML;
            }
          });

          // Re-bind variant data (JSON may be updated)
          const newJsonEl = pickerSrc.querySelector('[type="application/json"]');
          const oldJsonEl = pickerDest.querySelector('[type="application/json"]');
          if (newJsonEl && oldJsonEl) {
            oldJsonEl.textContent = newJsonEl.textContent;
            this.variantData = null; // Force re-parse on next updateMasterId
          }

          // Reset bound flag so dropdowns can be re-initialized
          pickerDest.querySelectorAll('.searchable-variant-container').forEach(c => {
            delete c._customPickerBound;
          });
          this.initSearchableDropdowns();
        }

        // --- Publish for other theme components (sticky bar, etc.) ---
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId, html, variant: this.currentVariant }
          });
        }
      });
  }

  // ============================================================
  // UTILITY
  // ============================================================

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    return this.variantData;
  }
}

customElements.define('custom-variant-picker', CustomVariantPicker);

// ============================================================================
// VariantSelects — kept here so that VariantHover (global.js) can extend it.
// VariantSelects is the original theme class. Our CustomVariantPicker is used
// on the product page; VariantSelects remains available for VariantHover
// (collection card hover-to-preview) and any other theme component that
// references it.
// ============================================================================
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.updateSelectedSwatchValue(event);
    this.toggleAddButton(true, '', false);
    this.updateSoldOutBadge();
    this.updateVariantValue(event);
    this.updateSku();
    this.updateStockCountdown();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      if (this.dataset.layout == 'card') {
        this.updateMedia();
      }
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateCardURL();

      // Sequence-based gallery filtering
      document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)?.filterMedia?.(this.currentVariant.id);
    }
  }

  updateVariantValue(event) {
    const target = event?.target;
    if (!target) return;
    if (this.dataset.layout == 'button') {
      const legendSpan = target.closest('fieldset')?.querySelector('legend span');
      if (legendSpan) legendSpan.innerHTML = target.value;
    }
    if (this.dataset.layout == 'card') {
      const fieldset = target.closest('fieldset');
      if (!fieldset) return;
      const legend = fieldset.querySelector('.form__label span');
      if (legend) legend.innerHTML = target.value;
    }
    const searchableContainer = target.closest('.searchable-variant-container');
    if (searchableContainer) {
      const labelSpan = searchableContainer.querySelector('.form__label span');
      if (labelSpan) labelSpan.innerHTML = target.value;
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') return element.value;
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => this.options[index] === option).includes(false);
    });
  }

  updateSoldOutBadge() {
    const card = this.closest('.card-wrapper');
    const soldOutBadge = card?.querySelectorAll('.soldout_product_badge');
    if (soldOutBadge && soldOutBadge.length == 1) {
      const isAvailable = this.currentVariant && this.currentVariant.available;
      soldOutBadge[0].classList.toggle('hidden', isAvailable);
    }
  }

  updateSelectedSwatchValue({ target }) {
    if (!target) return;
    const { name, value, tagName } = target;
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = this.querySelector(`[data-selected-dropdown-swatch="${name}"] > .swatch`);
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }
      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = this.querySelector(`[data-selected-swatch-value="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  updateSku() {
    if (!this.currentVariant) return;
    let productMain = this.closest('.product');
    if (productMain && productMain.querySelector('.variant-sku') != null) {
      productMain.querySelector('.variant-sku').innerHTML = this.currentVariant.sku;
    }
  }

  updateStockCountdown() {
    if (!this.currentVariant) return;
    let productMain = this.closest('.product');
    if (!productMain || !productMain.querySelector('.wbstockcount')) return;
    const variantsDataEl = productMain.querySelector('#product-variants');
    const stockConfigEl  = productMain.querySelector('#product-stock-countdown-config');
    if (!variantsDataEl || !stockConfigEl) return;
    const variantsData = JSON.parse(variantsDataEl.innerHTML);
    const stockCountdownConfig = JSON.parse(stockConfigEl.innerHTML);
    const variant = this.currentVariant;
    const countdownWrapper = productMain.querySelector('.wbstockcount');
    if (variantsData[variant.id]['q'] > 0 && variant.inventory_management == 'shopify') {
      productMain.querySelector('.wbstockcount').classList.remove('hidden');
      productMain.querySelector('.wbstockinfo-bar').classList.remove('hidden');
      if (variantsData[variant.id]['q'] > stockCountdownConfig.limited_stock_cut_off) {
        countdownWrapper.querySelector('.wbstockinfo').classList.remove('wbstockinfo_low', 'wbstockinfo_soon');
        countdownWrapper.querySelector('.wbstockinfo').classList.add('wbstockinfo_high');
        countdownWrapper.querySelector('.wbstockinfo').innerHTML = stockCountdownConfig.instock_message.replace('{stock_number}', variantsData[variant.id]['q']);
        countdownWrapper.querySelector('.wbstockinfo-probar').style.width = ((variantsData[variant.id]['q'] * 100) / stockCountdownConfig.max_stock) + '%';
      } else {
        countdownWrapper.querySelector('.wbstockinfo').classList.replace('wbstockinfo_soon', 'wbstockinfo_low');
        countdownWrapper.querySelector('.wbstockinfo').classList.add('wbstockinfo_low');
        countdownWrapper.querySelector('.wbstockinfo').innerHTML = stockCountdownConfig.limited_stock_message.replace('{stock_number}', variantsData[variant.id]['q']);
        countdownWrapper.querySelector('.wbstockinfo-probar').style.width = ((variantsData[variant.id]['q'] * 100) / stockCountdownConfig.max_stock) + '%';
      }
    } else if (variantsData[variant.id]['p'] == 'continue') {
      productMain.querySelector('.wbstockcount').classList.remove('hidden');
      productMain.querySelector('.wbstockinfo-bar').classList.add('hidden');
      countdownWrapper.querySelector('.wbstockinfo').classList.remove('wbstockinfo_low', 'wbstockinfo_high');
      countdownWrapper.querySelector('.wbstockinfo').classList.add('wbstockinfo_soon');
      countdownWrapper.querySelector('.wbstockinfo').innerHTML = stockCountdownConfig.continue_selling_message;
    } else {
      productMain.querySelector('.wbstockcount').classList.add('hidden');
    }
  }

  updateMedia(html) {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    if (this.dataset.layout == 'card') {
      const card = this.closest('.card');
      if (!card) return;
      const newMedia = card.querySelector(
        `[data-media-id="${this.dataset.section}-${this.dataset.product}-${this.currentVariant.featured_media.id}"]`
      );
      if (!newMedia) return;
      const parent = newMedia.parentElement;
      if (parent.firstChild == newMedia) return;
      parent.prepend(newMedia);
    } else {
      if (!html) return;
      const mediaGallerySource      = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
      const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
      const refreshSourceData = () => {
        const items = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
        const set   = new Set(items.map(item => item.dataset.mediaId));
        const map   = new Map(items.map((item, index) => [item.dataset.mediaId, { item, index }]));
        return [items, set, map];
      };
      if (mediaGallerySource && mediaGalleryDestination) {
        let [srcItems, srcSet, srcMap] = refreshSourceData();
        const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
        const destSet   = new Set(destItems.map(({ dataset }) => dataset.mediaId));
        let shouldRefresh = false;
        for (let i = destItems.length - 1; i >= 0; i--) {
          if (!srcSet.has(destItems[i].dataset.mediaId)) { mediaGallerySource.prepend(destItems[i]); shouldRefresh = true; }
        }
        for (let i = 0; i < srcItems.length; i++) {
          if (!destSet.has(srcItems[i].dataset.mediaId)) { srcItems[i].remove(); shouldRefresh = true; }
        }
        if (shouldRefresh) [srcItems, srcSet, srcMap] = refreshSourceData();
        destItems.forEach((destItem, destIndex) => {
          const srcData = srcMap.get(destItem.dataset.mediaId);
          if (srcData && srcData.index !== destIndex) {
            mediaGallerySource.insertBefore(srcData.item, mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`));
            [srcItems, srcSet, srcMap] = refreshSourceData();
          }
        });
      }
      document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)
        ?.setActiveMedia?.(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);
      const modalContent    = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      const newModalContent = html.querySelector('product-modal');
      if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
    }
  }

  updateCardURL() {
    if (this.dataset.layout != 'card') return;
    const card = this.closest('.card');
    if (!card) return;
    const productURL  = this.updateURLParameter(this.dataset.url, 'variant', this.currentVariant.id);
    const anchor      = card.querySelector('.card__media a');
    const titleAnchor = card.querySelector('.card__heading a');
    const quickview   = card.querySelector('.js-wbquickview-link');
    if (anchor)      anchor.setAttribute('href', productURL);
    if (titleAnchor) titleAnchor.setAttribute('href', productURL);
    if (quickview)   quickview.setAttribute('variant-id', this.currentVariant.id);
  }

  updateURLParameter(url, param, paramVal) {
    let newAdditionalURL = '';
    const tempArray = url.split('?');
    const baseURL = tempArray[0];
    const additionalURL = tempArray[1];
    let temp = '';
    if (additionalURL) {
      const parts = additionalURL.split('&');
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].split('=')[0] !== param) { newAdditionalURL += temp + parts[i]; temp = '&'; }
      }
    }
    return baseURL + '?' + newAdditionalURL + temp + param + '=' + paramVal;
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    if (this.dataset.layout == 'card') {
      const card = this.closest('.card');
      const productForms = card.querySelectorAll(`#ProductInfo-${this.dataset.section}-${this.dataset.product}`);
      productForms.forEach((productForm) => {
        productForm.querySelectorAll('input[name="id"]').forEach(el => { el.value = this.currentVariant.id; });
        productForm.querySelectorAll('select[name="id"]').forEach(el => { el.value = this.currentVariant.id; });
        Array.from(this.querySelectorAll('fieldset')).forEach(fieldset => {
          fieldset.querySelectorAll('label').forEach(label => label.classList.remove('active'));
        });
        const container = this.closest('.grid__item');
        if (!container) return;
        const optionData       = container.querySelectorAll('select option');
        const regularPrice     = container.querySelector('.price .price__container .price__regular .price-item--regular');
        const saleRegularPrice = container.querySelector('.price .price__container .price__sale .price-item--sale');
        const salePrice        = container.querySelector('.price .price__container .price__sale .price-item--regular');
        const wbunitPrice      = container.querySelector('.price .price__container .unit-price .cardunitp');
        const wbunitValue      = container.querySelector('.price .price__container .unit-price .cardunitv');
        const wbPercentBadge   = container.querySelector('.card__badge .percent__badge-sale');
        const wbAmountBadge    = container.querySelector('.card__badge .amount__badge-sale');
        optionData.forEach((data) => {
          if (data.value != this.currentVariant.id) return;
          if (data.dataset.cprice && data.dataset.price != data.dataset.cprice) {
            if (saleRegularPrice) saleRegularPrice.innerHTML = data.dataset.price;
            if (salePrice)        salePrice.innerHTML = data.dataset.cprice;
            if (data.dataset.damount && wbAmountBadge) { wbAmountBadge.innerHTML = data.dataset.damount; wbAmountBadge.classList.remove('hidden'); }
            if (data.dataset.percent && wbPercentBadge) { wbPercentBadge.innerHTML = data.dataset.percent; wbPercentBadge.classList.remove('hidden'); }
            container.querySelector('.price')?.classList.add('price--on-sale');
          } else {
            if (regularPrice) regularPrice.innerHTML = data.dataset.price;
            if (wbPercentBadge) wbPercentBadge.classList.add('hidden');
            if (wbAmountBadge)  wbAmountBadge.classList.add('hidden');
            container.querySelector('.price')?.classList.remove('price--on-sale');
          }
          if (data.dataset.unitprice && wbunitPrice) { wbunitPrice.innerHTML = data.dataset.unitprice; container.querySelector('.price')?.classList.add('price--on-sale'); }
          if (data.dataset.unitvalue && wbunitValue) { wbunitValue.innerHTML = data.dataset.unitvalue; container.querySelector('.price')?.classList.add('price--on-sale'); }
        });
      });
      return;
    }
    document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
      .forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input) { input.value = this.currentVariant.id; input.dispatchEvent(new Event('change', { bubbles: true })); }
      });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    let sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    if (this.dataset.layout == 'card') {
      fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          sectionId = `${sectionId}-${this.dataset.product}`;
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant: this.currentVariant } });
          }
        });
      return;
    }

    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destinations = document.querySelectorAll(`#price-${this.dataset.section}`);
        const sources = html.querySelectorAll(`#price-${sectionId}`);
        const skuSource = html.getElementById(`Sku-${sectionId}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${sectionId}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
        const volumePricingSource = html.getElementById(`Volume-${sectionId}`);

        if (this.dataset.layout != 'card') this.updateMedia(html);

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`);
        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        if (qtyRules) qtyRules.classList.remove('hidden');

        if (sources.length == destinations.length && sources.length != 0) {
          let idx = 0;
          for (let source of sources) { destinations[idx].innerHTML = source.innerHTML; idx++; }
        }
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }
        if (volumePricingSource && volumePricingDestination) volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('visibility-hidden', pricePerItemSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);
        if (price) price.classList.remove('hidden');
        if (inventoryDestination) inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true, window.variantStrings.soldOut);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId, html, variant: this.currentVariant } });
        }
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    var selector = `product-form-${this.dataset.section}`;
    if (this.dataset.layout == 'card') selector = `product-form-${this.dataset.section}-${this.dataset.product}`;
    const productForm = document.getElementById(selector);
    if (!productForm) return;
    const addButton     = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;
    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }
    if (!modifyClass) return;
  }

  setUnavailable() {
    var selector = `product-form-${this.dataset.section}`;
    if (this.dataset.layout == 'card') selector = `product-form-${this.dataset.section}-${this.dataset.product}`;
    const button = document.getElementById(selector);
    if (!button) return;
    const addButton     = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const inventory     = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku           = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem  = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote    = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable   = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules      = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (inventory)   inventory.classList.add('hidden');
    if (sku)         sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote)  volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules)    qtyRules.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

// VariantRadios extends VariantSelects (used in theme for radio-button style pickers)
class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);
